/**
 * Ładunek zapisu karty produktu z panelu administratora (UD-23).
 *
 * Do UD-23 edycja wysyłała cały formularz: nazwę (`trim`), kod, organizację i resztę pól — także te, których
 * człowiek nie ruszył. Od UD-23 kartę prowadzoną przez system zewnętrzny (mapa `prowadzi = true`) chroni
 * wyzwalacz `zz_kartoteka_chron_produkt`: zmiana nazwy, kodu albo aktywności z panelu to odmowa 23001.
 * Formularz wysyłający nazwę przyciętą z „Chleb ” na „Chleb” zmieniałby więc pole prowadzone, choć człowiek
 * zmieniał tylko kategorię — i dostawałby odmowę za coś, czego nie zrobił. Dlatego edycja wysyła wyłącznie pola,
 * które człowiek zmienił (porównanie surowych wartości formularza z wartościami z chwili otwarcia), a nigdy
 * `organization_id` (karta nie zmienia organizacji z tego ekranu).
 *
 * Temperatio A+ (migracja 839, 26.10.2026): ten ładunek nie niesie już pól Temperatio (cel, mieszanie, kwas) —
 * ustawienia Temperatio mają JEDNO okno, panel Temperatio, i jednego pisarza (`waterandice_produkt_zapisz`), bo
 * dwa edytory już się rozjechały (plan `docs/10-TEMPERATIO-NATIVE/11-…`, §4.5). Edycja nie niesie też
 * `module_type`: to moduł, który kartę ZAŁOŻYŁ, a członkostwo w Temperatio bez znacznika liczy się z niego, więc
 * zmiana pochodzenia po cichu zdejmowała kartę z Temperatio i ze starych telefonów albo ją tam dokładała.
 */

export interface FormularzProduktu {
  name: string;
  code: string;
  category: string;
  description: string;
  /** Moduł zakładający — wybierany wyłącznie przy zakładaniu karty. */
  module_type: string;
}

/** Wartości wysyłane do bazy — ta sama normalizacja przy dodaniu i przy edycji. */
function znormalizowane(f: FormularzProduktu): Record<keyof FormularzProduktu, unknown> {
  return {
    name: f.name.trim(),
    code: f.code.trim() || null,
    category: f.category.trim() || null,
    description: f.description.trim() || null,
    module_type: f.module_type || null,
  };
}

/** Pola karty, które edycja może zmienić. */
const POLA_KARTY = ['name', 'code', 'category', 'description'] as const;

/** Ładunek nowej karty: pola karty i moduł zakładający, z organizacją — bez kolumn Temperatio i `module_data`. */
export function ladunekDodania(
  formularz: FormularzProduktu,
  organizacjaId: string
): Record<string, unknown> {
  const n = znormalizowane(formularz);
  const ladunek: Record<string, unknown> = { organization_id: organizacjaId };
  for (const pole of POLA_KARTY) ladunek[pole] = n[pole];
  ladunek.module_type = n.module_type;
  return ladunek;
}

/**
 * Ładunek edycji: tylko pola karty zmienione względem formularza z chwili otwarcia (`przed`), znormalizowane jak
 * przy dodaniu. Nigdy `module_type` ani pola Temperatio. Pusty wynik = nic do zapisania.
 */
export function zmianyFormularza(
  przed: FormularzProduktu,
  po: FormularzProduktu
): Record<string, unknown> {
  const n = znormalizowane(po);
  const zmiany: Record<string, unknown> = {};
  for (const pole of POLA_KARTY) {
    if (po[pole] !== przed[pole]) zmiany[pole] = n[pole];
  }
  return zmiany;
}
